"use client";

import { Minus, Plus, Trash2 } from "lucide-react";
import { __ } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import {
  extractCurrencySymbol,
  formatPrice,
  getMedusaProductPrice,
  isSaleProduct,
  removeCurrencySymbol,
} from "@/lib/utils";
import Image from "next/image";
import { StoreCartLineItem, StoreProduct } from "@medusajs/types";
import { ProductDocument } from "@/prismicio-types";
import { useState } from "react";
import { useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { toast } from "@/components/atoms/toast";
import ProductBadge from "@/components/atoms/product-badge";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import { PriceToastDropdown } from "@/components/atoms/price-toast-dropdown";
import { Badge } from "@/components/ui/badge";
import {
  getCartQuantityStep,
  normalizeConditioning,
  toLotQuantity,
  resolveProductConditioning,
  toLotPrice,
} from "@/lib/product-units";

type SheetCartItemProps = {
  medusaProduct: StoreProduct;
  quantity?: number;
  cartItem: StoreCartLineItem | undefined;
  locale: Locales;
  prismicProduct: ProductDocument;
};

export function SheetCartItem({ medusaProduct, quantity, locale, prismicProduct, cartItem }: SheetCartItemProps) {
  const [count, setCount] = useState(quantity || 1);

  const { removeItem } = useRemoveCartItem({
    onSuccess: () => toast.success({ title: __("Item removed from cart!", locale) }),
    onError: (error) => {
      console.error("Error removing item from cart:", error, cartItem);
      toast.error({ title: __("Error removing item from cart", locale) });
    },
  });

  const { updateQuantity } = useUpdateCartItem({
    onSuccess: () => toast.success({ title: __("Quantity updated successfully", locale) }),
    onError: (err) => {
      console.error("Error updating quantity", err);
      toast.error({ title: __("Failed to update quantity", locale) });
    },
  });

  const handleQuantityChange = (newQuantity: number) => {
    if (cartItem?.id && newQuantity !== cartItem.quantity) {
      updateQuantity(cartItem.id, newQuantity);
    }
  };

  const handleDecrease = () => {
    setCount((prev) => {
      const next = prev > quantityStep ? prev - quantityStep : prev;
      handleQuantityChange(next);
      return next;
    });
  };

  const handleIncrease = () => {
    setCount((prev) => {
      const next = prev + quantityStep;
      handleQuantityChange(next);
      return next;
    });
  };

  const productConditioning = resolveProductConditioning(
    medusaProduct?.metadata?.conditioning,
    medusaProduct?.metadata?.conditioning_bac
  );
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const isFullImage = prismicProduct?.data?.image_full_size_card;
  const isPriceDynamic = medusaProduct?.metadata?.is_price_dynamic as boolean;
  const isRange = medusaProduct?.metadata?.is_surface_based as boolean;
  const hasMultipleVariants = (medusaProduct?.variants?.length ?? 0) > 1;

  // We resolve which variant is actually in the cart by matching cartItem.variant_id
  // against the product's variants array. This is the ONLY source of truth for
  // what price to display — we never read from the store here because the store
  // reflects the user's current PDP selection, not what's in the cart.
  const cartVariant = hasMultipleVariants
    ? medusaProduct?.variants?.find((v) => v.id === cartItem?.variant_id)
    : medusaProduct?.variants?.[0];

  // Cart variant price (what was actually priced when added to cart)
  const cartVariantPrice = cartVariant?.calculated_price?.calculated_amount ?? 0;

  // For single-variant products, fall back to the standard price util
  const singleVariantPrice = medusaProduct?.variants?.[0]?.calculated_price?.calculated_amount || 0;

  // Formatted prices
  const formattedUnitPrice = hasMultipleVariants
    ? formatPrice(toLotPrice(cartVariantPrice, productConditioning), { showDecimals: true })
    : getMedusaProductPrice(medusaProduct);

  const formattedTotalPrice = hasMultipleVariants
    ? formatPrice(cartVariantPrice * count, { showDecimals: true })
    : formatPrice(singleVariantPrice * count);

  return (
    <article className="mb-6 space-y-3" role="group">
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-2">
          <figure
            className="relative flex size-12 items-center justify-center overflow-hidden rounded-md bg-[#f9f7f5]"
            aria-label={__("Product image", locale)}
          >
            {isFullImage ? (
              <Image
                src={
                  sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                  prismicProduct?.data.images[0]?.image.url ||
                  prismicProduct?.data.featuredImage.url ||
                  ""
                }
                fill
                className="absolute object-cover mix-blend-multiply outline-offset-2 transition-transform duration-300 group-hover/card:scale-105"
                alt={prismicProduct?.data.title || medusaProduct.title}
              />
            ) : (
              <div className="relative size-8">
                <Image
                  src={
                    sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                    prismicProduct?.data.images[0]?.image.url ||
                    prismicProduct?.data.featuredImage.url ||
                    ""
                  }
                  fill
                  className="absolute object-contain mix-blend-multiply outline-offset-2 transition-transform duration-300 group-hover/card:scale-105"
                  alt={prismicProduct?.data.title || medusaProduct.title}
                />
              </div>
            )}
          </figure>
          {isSaleProduct(medusaProduct) && (
            <Badge variant="festi" className="-mt-1 px-1 text-[10px]">
              {__("To sell", locale)}
            </Badge>
          )}
        </div>

        <div className="flex-1 space-y-1">
          <div className="flex w-full items-center justify-between gap-2">
            <h3 className="font-medium text-[#111928]">{prismicProduct?.data.title || medusaProduct.title}</h3>
            {/* Price-on-demand badge for single-variant range/dynamic products */}
            {(isRange || isPriceDynamic) && !hasMultipleVariants && <PriceToastDropdown locale={locale} />}
          </div>

          {/* ── Unit price row ─────────────────────────────────────────── */}
          {((!isRange && !isPriceDynamic) || hasMultipleVariants) && (
            <div className="flex w-full items-center justify-baseline">
              <p className="text-sm font-semibold text-[#111928]">
                {formattedUnitPrice}
                {physicalPieceCount > 1 && (
                  <span className="text-primary-light text-xs">
                    {" "}
                    / {productConditioning} {__("piece", locale)}
                  </span>
                )}
              </p>
              {physicalPieceCount > 1 && (
                <ProductBadge
                  className="ml-auto max-w-full whitespace-normal text-right"
                  text={formatProductQuantity(count, productConditioning, locale)}
                />
              )}
            </div>
          )}

          {/* ── Detail rows (qty + price) ──────────────────────────────── */}
          <dl className="space-y-1">
            <div className="text-primary-light flex items-center gap-4 text-xs">
              <dt>{__("QTY", locale)}:</dt>
              <dd className="text-dark-primary ml-auto">
                {formatProductQuantity(count, productConditioning, locale)}
              </dd>
            </div>

            {/* Total price — shown for standard and multi-variant products */}
            {((!isRange && !isPriceDynamic) || hasMultipleVariants) && (
              <div className="text-primary-light flex items-center gap-4 text-xs">
                <dt>{__("Price", locale)}:</dt>
                <dd className="text-dark-primary ml-auto">
                  {removeCurrencySymbol(formattedTotalPrice)}
                  <sup>{extractCurrencySymbol(formattedTotalPrice)}</sup>
                </dd>
              </div>
            )}
          </dl>

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => removeItem(cartItem?.id || "")}
              className="text-primary-light flex cursor-pointer items-center gap-1 text-xs underline transition-colors hover:text-[#111928]"
            >
              <Trash2 className="h-3 w-3" />
              {__("Remove", locale)}
            </button>

            <div
              className="flex items-center gap-3 rounded border border-[#DEDEDE]"
              role="group"
              aria-label={__("Quantity controls", locale)}
            >
              <button
                onClick={handleDecrease}
                disabled={count <= quantityStep}
                className="hover:text-dark-primary disabled:hover:text-primary-light text-primary-light flex h-6 w-6 cursor-pointer items-center justify-center disabled:opacity-50"
              >
                <Minus strokeWidth={3} className="h-3 w-3" />
              </button>

              <span className="min-w-[20px] text-center text-sm font-medium text-[#111928]" role="status">
                {toLotQuantity(count, productConditioning)}
              </span>

              <button
                onClick={handleIncrease}
                className="hover:text-dark-primary text-primary-light flex h-6 w-6 cursor-pointer items-center justify-center"
              >
                <Plus strokeWidth={3} className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
